import { useQuery } from "@tanstack/react-query";
import { useRef, type ReactNode } from "react";
import { AREA_META, type AreaCode } from "../../shared/types";
import { fetchStoreMeta, type StoreMetaResponse } from "../lib/api";
import { cn } from "../lib/classNames";
import type { StoresFilter } from "../hooks/useStores";
import { PinCountSelect } from "./PinCountSelect";

interface Props {
  filter: StoresFilter;
  onChange: (filter: StoresFilter) => void;
  /** Controlled input value — reflects every keystroke (including IME 未確定)。 */
  nameQuery: string;
  /** 表示用 state の更新ハンドラ。全文字で発火する。 */
  onNameQueryChange: (value: string) => void;
  /**
   * 「フィルタに適用する値」の更新ハンドラ。IME 未確定中は呼ばれず、
   * 変換確定 / クリア / 非 IME 入力時にのみ発火する。progressive resolve の
   * 無駄な再起動を避けるために use される。
   */
  onNameQueryCommit: (value: string) => void;
  /**
   * 検索ワードが確定済みで、結果に対して area/year/minSelections フィルタが
   * 効かない状態かどうか。true なら下のチップ群を dim 表示して、無効である
   * ことをユーザーに伝える。状態自体は維持されるのでチップ操作は引き続き可能。
   */
  searchActive: boolean;
  /** 閉店 / 移転店舗を結果に含めるか。 */
  showInactive: boolean;
  onShowInactiveChange: (next: boolean) => void;
  /**
   * マップ上のピン表示モード。
   * true: `@googlemaps/markerclusterer` でクラスタリング表示 (デフォルト)。
   * false: 全ピンを個別表示。
   */
  clusterMode: boolean;
  onClusterModeChange: (next: boolean) => void;
  compact?: boolean;
  /**
   * compact mode のときに年度 / 選出回数 Row も展開して表示するか。
   * 非 compact (PC sidebar) では常に展開状態と同じになるので無視される。
   */
  expanded?: boolean;
  /**
   * compact mode 時、要素群の下にフッタとして描画する展開トグルのハンドラ。
   * 渡された場合のみ「条件をさらに表示 / 条件を閉じる」ボタンが描画される。
   * 非 compact では描画しない (PC sidebar では常に展開済み)。
   */
  onExpandToggle?: () => void;
  /**
   * SP (compact) で SearchInput を表示するかどうか。
   * デフォルト (undefined) は常に表示。toggle ボタンを外から付けて
   * 「タップして検索を開く」動線にしたいときだけ false で隠す。
   * PC (非 compact) では無視される (PC は常に表示)。
   */
  searchVisible?: boolean;
  /**
   * SP (compact) 時、エリア行の右端に並べる追加コントロール (検索トグル等)。
   * 縦スペース節約のため、専用行を作らずチップ行と同じ行に差し込む。
   */
  trailingActions?: ReactNode;
  /**
   * SP (compact) で「ピン表示上限」select を expanded セクション内に描画するための props。
   * 渡された場合のみレンダリングされる。PC は Header に直接持っているので
   * 渡さない (= FilterBar 内には出ない)。
   */
  maxPinCount?: number;
  onMaxPinCountChange?: (next: number) => void;
  minPinCount?: number;
  maxPinCountLimit?: number;
}

export function FilterBar({
  filter,
  onChange,
  nameQuery,
  onNameQueryChange,
  onNameQueryCommit,
  searchActive,
  showInactive,
  onShowInactiveChange,
  clusterMode,
  onClusterModeChange,
  compact = false,
  expanded = false,
  onExpandToggle,
  searchVisible = true,
  trailingActions,
  maxPinCount,
  onMaxPinCountChange,
  minPinCount,
  maxPinCountLimit,
}: Props) {
  const meta = useQuery<StoreMetaResponse>({
    queryKey: ["store-meta"],
    queryFn: fetchStoreMeta,
    staleTime: 1000 * 60 * 60,
  });

  const areas = (meta.data?.areas ?? []) as AreaCode[];
  const years = meta.data?.years ?? [];

  // 「年度 / 選出回数 Row を表示するか」の判定。
  // - PC (非 compact): 常に表示
  // - SP compact: expanded のときだけ表示 (画面占有を抑える)
  const showAdvanced = !compact || expanded;

  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        compact
          ? cn(
              // SP: backdrop-blur を外し (200 マーカーの blur と重ねて合成すると
              // SP GPU がコマ落ちする)、shadow も軽量化。背景は半不透明のまま。
              "rounded-2xl border border-white/10 bg-slate-900/95 px-3 py-2.5 shadow-lg shadow-black/40",
              // 展開時は SP 画面の高さに収まりきらないので内部スクロールに倒す。
              // 160px は Header (~50) + safe-top + filter overlay padding + 余白の合計。
              expanded &&
                "thin-scroll max-h-[calc(100dvh-160px)] overflow-y-auto",
            )
          : "border-b border-white/10 p-4",
      )}
    >
      {/* SearchInput の配置:
          - PC (非 compact): 常時表示。フィルタ群の最上段。
          - SP (compact): `searchVisible` が true のときだけ描画される独立行。
            デフォルトは false 扱い (App.tsx 側で外部 state で制御) のため
            縦スペースを食わない。展開トグル + 検索トグルで必要時だけ開く。 */}
      {(!compact || searchVisible) && (
        <SearchInput
          value={nameQuery}
          onChange={onNameQueryChange}
          onCommit={onNameQueryCommit}
          compact={compact}
        />
      )}

      {searchActive && (
        <div
          role="note"
          className="flex items-center gap-1.5 rounded-md border border-amber-400/30 bg-amber-500/10 px-2.5 py-1 text-[10px] leading-tight text-amber-200"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="shrink-0"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
          <span>ワード検索中はエリア / 年度 / 選出回数の絞り込みは無効です</span>
        </div>
      )}

      <div
        className={cn(
          "flex flex-col gap-3 transition-opacity",
          searchActive && "opacity-40",
        )}
        aria-disabled={searchActive}
      >
      {/* SP (compact) はエリアチップ行の右側に検索トグル等を inline で並べる。
          flex-wrap する chip 群を flex-1 min-w-0 のコンテナに閉じ込め、
          右端の trailingActions は shrink-0 で常に最上段に留める。
          PC (非 compact) では trailingActions を使わないので単純な <Row> として描画する。 */}
      {compact && trailingActions ? (
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <Row label={expanded ? "エリア" : undefined}>
              <Chip
                active={!filter.area}
                onClick={() => onChange({ ...filter, area: undefined })}
                tone="slate"
              >
                すべて
              </Chip>
              {areas.map((area) => (
                <Chip
                  key={area}
                  active={filter.area === area}
                  onClick={() => onChange({ ...filter, area })}
                  tone="slate"
                >
                  {AREA_META[area].label}
                </Chip>
              ))}
            </Row>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {trailingActions}
          </div>
        </div>
      ) : (
        <Row label="エリア">
          <Chip
            active={!filter.area}
            onClick={() => onChange({ ...filter, area: undefined })}
            tone="slate"
          >
            すべて
          </Chip>
          {areas.map((area) => (
            <Chip
              key={area}
              active={filter.area === area}
              onClick={() => onChange({ ...filter, area })}
              tone="slate"
            >
              {AREA_META[area].label}
            </Chip>
          ))}
        </Row>
      )}

      {showAdvanced && years.length > 0 && (
        <Row label="選出年">
          <Chip
            active={filter.year === undefined}
            onClick={() => onChange({ ...filter, year: undefined })}
            tone="amber"
          >
            全年度
          </Chip>
          {years.map((y) => (
            <Chip
              key={y}
              active={filter.year === y}
              onClick={() => onChange({ ...filter, year: y })}
              tone="amber"
            >
              {y}
            </Chip>
          ))}
        </Row>
      )}

      {showAdvanced && (
        <Row label="選出回数">
          {/* 百名店の選出回数は最大 9 回。 2〜9 回以上を 1 ステップずつ並べる。 */}
          {[undefined, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <Chip
              key={n ?? "all"}
              active={filter.minSelections === n}
              onClick={() => onChange({ ...filter, minSelections: n })}
              tone="rose"
            >
              {n === undefined ? "指定なし" : `${n}回以上`}
            </Chip>
          ))}
        </Row>
      )}

      {/* SP (compact) のみ: ピン表示上限 select を condition 内に表示。
          PC は Header に持つので props 自体が渡されず、ここは null になる。 */}
      {compact &&
        showAdvanced &&
        typeof maxPinCount === "number" &&
        onMaxPinCountChange &&
        typeof minPinCount === "number" &&
        typeof maxPinCountLimit === "number" && (
          <Row label="ピン表示上限">
            <PinCountSelect
              value={maxPinCount}
              onChange={onMaxPinCountChange}
              min={minPinCount}
              max={maxPinCountLimit}
            />
          </Row>
        )}
      {/* 末尾トグル群: 閉店・移転 / クラスタリング表示。
          どちらも「絞り込み条件」ではなく「表示方法」の切替なので、
          他のチップ群とは独立に末尾にまとめて配置する。
          compact (SP) では ▾ 展開時にのみ表示し、縦スペースを節約する。 */}
      {showAdvanced && (
        <div className="flex flex-wrap gap-2">
          <InactiveToggle
            showInactive={showInactive}
            onChange={onShowInactiveChange}
          />
          <ClusterToggle
            clusterMode={clusterMode}
            onChange={onClusterModeChange}
          />
        </div>
      )}
      </div>

      {/* compact mode のフッタトグル: 要素群の下に「条件をさらに表示 / 条件を閉じる」。
          PC (非 compact) では sidebar に全項目が常時表示されているため不要。
          負マージン -mx-3 -mb-2.5 でカードの左右下端まで延ばし、上 border の
          フッタストリップとして描画する。 */}
      {compact && onExpandToggle && (
        <button
          type="button"
          onClick={onExpandToggle}
          aria-expanded={expanded}
          className="-mx-3 -mb-2.5 mt-1 flex items-center justify-center gap-1.5 border-t border-white/10 px-3 py-2 text-[11.5px] font-medium text-slate-300 transition hover:bg-white/5 active:bg-white/10"
        >
          <span>{expanded ? "条件を閉じる" : "条件をさらに表示"}</span>
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className={cn("transition-transform", expanded && "rotate-180")}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * マップ上のピン表示モード切替トグル。
 * ON: クラスタリング表示 (近接ピンを集約)、OFF: 個別表示。
 * 大量ピン時は ON のほうが SP GPU 負荷が大幅に軽い。
 */
function ClusterToggle({
  clusterMode,
  onChange,
}: {
  clusterMode: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={clusterMode}
      onClick={() => onChange(!clusterMode)}
      className={cn(
        "inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-[11px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
        clusterMode
          ? "border-indigo-400/50 bg-indigo-500/15 text-indigo-100 hover:bg-indigo-500/25"
          : "border-white/15 bg-slate-800/70 text-slate-400 hover:bg-slate-800/90",
      )}
    >
      {/* アイコン: ON = 集合ドット、OFF = 単独ピン */}
      {clusterMode ? (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <circle cx="7" cy="7" r="3" />
          <circle cx="17" cy="8" r="2.5" />
          <circle cx="8" cy="17" r="2.5" />
          <circle cx="17" cy="17" r="3" />
        </svg>
      ) : (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="10" r="3" />
          <path d="M12 2a8 8 0 0 0-8 8c0 6 8 12 8 12s8-6 8-12a8 8 0 0 0-8-8z" />
        </svg>
      )}
      <span>
        ピン{clusterMode ? "集約" : "個別"}
        {!clusterMode && (
          <span className="ml-0.5 text-[9px] font-normal text-amber-300/90">
            (重たい)
          </span>
        )}
      </span>
    </button>
  );
}

function InactiveToggle({
  showInactive,
  onChange,
}: {
  showInactive: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={showInactive}
      onClick={() => onChange(!showInactive)}
      className={cn(
        "inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-[11px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
        showInactive
          ? "border-amber-400/50 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25"
          : "border-white/15 bg-slate-800/70 text-slate-400 hover:bg-slate-800/90",
      )}
    >
      {showInactive ? (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ) : (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      )}
      <span>閉店・移転を{showInactive ? "含む" : "除外"}</span>
    </button>
  );
}

function SearchInput({
  value,
  onChange,
  onCommit,
  compact,
}: {
  value: string;
  onChange: (value: string) => void;
  onCommit: (value: string) => void;
  compact: boolean;
}) {
  const composingRef = useRef(false);

  return (
    <div className="relative">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        type="text"
        inputMode="search"
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next);
          // IME 変換中は未確定文字列を下流に流さない。
          if (!composingRef.current) onCommit(next);
        }}
        onCompositionStart={() => {
          composingRef.current = true;
        }}
        onCompositionEnd={(e) => {
          composingRef.current = false;
          onCommit((e.target as HTMLInputElement).value);
        }}
        placeholder="店名で検索"
        aria-label="店名で検索"
        className={cn(
          "w-full rounded-full border border-white/10 bg-slate-800/80 pl-9 pr-8 text-slate-100 placeholder:text-slate-500 focus:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/20",
          compact ? "py-1.5 text-xs" : "py-2 text-[13px]",
        )}
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange("");
            onCommit("");
          }}
          aria-label="検索をクリア"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 transition hover:bg-white/10 hover:text-slate-200"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M6 6l12 12M18 6l-12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </span>
      )}
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  tone,
  children,
}: {
  active: boolean;
  onClick: () => void;
  tone: "slate" | "amber" | "rose";
  children: ReactNode;
}) {
  const activeCls = {
    slate: "bg-slate-100 text-slate-900 border-slate-100",
    amber: "bg-amber-400 text-slate-950 border-amber-300",
    rose: "bg-rose-500 text-white border-rose-400",
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition",
        active
          ? activeCls
          : "border-white/10 bg-slate-800/80 text-slate-200 hover:bg-slate-700",
      )}
    >
      {children}
    </button>
  );
}
